import React, { useState, useContext, useMemo, useEffect, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { CloseOutlined } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { DatasetType, SeriesId, SeriesValueFormatterContext } from '@mui/x-charts/internals';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import { LineSeriesType, BarSeriesType } from '@mui/x-charts';
import { UserContext } from '../../../../context/UserContext';
import { formatValue } from '../../../../helpers/helpers';
import { NO_VALUE_MARK } from '../../dashboardHelpers';

interface NavigationPopupProps {
  open: boolean;
  onClose: () => void;
  dataset: DatasetType;
  series: (LineSeriesType | BarSeriesType)[];
  seriesIndex?: number;
  currency?: string;
}

const getLabel = (
  label: string | ((location: 'tooltip' | 'legend') => string) | undefined
) => {
  if (!label) return '';
  if (typeof label === 'string') return label;
  return label('tooltip');
};

interface TooltipRowProps {
  seriesId: SeriesId | undefined;
  formattedLabel: string;
  formattedValue: string | number | null | undefined;
  color: string | undefined;
}

const TooltipRow = ({ seriesId, formattedLabel, formattedValue, color }: TooltipRowProps) => {
  const theme = useTheme();
  

  return (
    <Box
      key={seriesId}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        height: 48,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            bgcolor: color,
            mr: 1,
            flexShrink: 0,
          }}
        />
        <Typography
          sx={[
            theme.typography.tooltipText,
            { whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' },
          ]}
        >
          {formattedLabel}
        </Typography>
      </Box>

      <Typography variant="body2" sx={{ marginLeft: 2, whiteSpace: 'nowrap' }}>
        {formattedValue}
     </Typography>
    </Box>
  );
};

const NavigationTooltip = ({
  open,
  onClose,
  dataset,
  series,
  seriesIndex = 0,
  currency
}: NavigationPopupProps) => {
  const { locale } = useContext(UserContext);
  const theme = useTheme();
  const { t } = useTranslation();
  const dates = useMemo(() => dataset.map(d => d.dateTime), [dataset]);
  const totalPages = dates.length;

  const [page, setPage] = useState(0);

  useEffect(() => {
    if (open) {
      const validIndex = Math.max(0, Math.min(seriesIndex, dataset.length - 1));
      setPage(validIndex);
    }
  }, [open, seriesIndex, dataset.length]);

  const selectedDate = dates[page] ?? null;

  const datapoint = useMemo(
    () =>
      selectedDate
        ? dataset.find(d => String(d.dateTime) === String(selectedDate))
        : undefined,
    [dataset, selectedDate]
  );

  const seriesItems = useMemo(
    () =>
      series.map((serie, index) => {
        const rawValue = serie?.dataKey ? datapoint?.[serie.dataKey] : undefined;
        const numericValue = typeof rawValue === 'number' ? rawValue : null;
        const roundedValue =
          numericValue != null ? Math.round(numericValue * 100) / 100 : undefined;


        return {
          seriesId: serie.id,
          formattedLabel: getLabel(serie.label),
          formattedValue: serie?.valueFormatter
            ? serie.valueFormatter(numericValue, {
              dataIndex: index,
            } as SeriesValueFormatterContext)
            : roundedValue,
          value: roundedValue ?? 0,
          color: serie.color,
          type: serie.type,
        };
      }),
    [series, datapoint]
  );


  const lineSeriesItems = useMemo(
    () => seriesItems.filter(item => item.type !== 'bar'),
    [seriesItems]
  );

  const barSeriesItems = useMemo(
    () => seriesItems.filter(item => item.type === 'bar'),
    [seriesItems]
  );

  const isOnlyBars = lineSeriesItems.length === 0;

  const orderedBarItems = useMemo(
    () => [...barSeriesItems].sort((a, b) => b.value - a.value),
    [barSeriesItems]
  );

  const volumeItem = useMemo(
    () =>
      orderedBarItems.find(item =>
        item.formattedLabel.toLowerCase().includes('volume')
      ),
    [orderedBarItems]
  );

  const barsWithoutVolume = useMemo(
    () =>
      isOnlyBars
        ? orderedBarItems.filter(item => item !== volumeItem)
        : barSeriesItems,
    [isOnlyBars, orderedBarItems, barSeriesItems, volumeItem]
  );

  const totalValue = useMemo(
    () => barsWithoutVolume.reduce((acc, item) => acc + item.value, 0),
    [barsWithoutVolume]
  );

  const handlePrev = useCallback(
    () => setPage(prev => (prev > 0 ? prev - 1 : prev)),
    []
  );

  const handleNext = useCallback(
    () => setPage(prev => (prev < totalPages - 1 ? prev + 1 : prev)),
    [totalPages]
  );

  return (
    <Dialog open={open} onClose={onClose} onClick={(e) => e.stopPropagation()}
      fullWidth maxWidth="xs">
      <DialogTitle sx={{ p: 1.5, backgroundColor: theme.palette.grey['4p'] }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '40px 1fr auto',
            alignItems: 'center',
          }}
        >
          <Box>
            {page !== 0 && (
              <IconButton size="small" onClick={handlePrev}>
                <ArrowBackIosIcon fontSize="small" sx={{ color: 'black' }} />
              </IconButton>
            )}
          </Box>

          <Typography variant="boldText" sx={{ textAlign: 'center' }}>
            {selectedDate
              ? Intl.DateTimeFormat(locale, {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              }).format(new Date(selectedDate))
              : '--'}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {page !== totalPages - 1 && (
              <IconButton size="small" onClick={handleNext}>
                <ArrowForwardIosIcon fontSize="small" sx={{ color: 'black' }} />
              </IconButton>
            )}
            <IconButton size="small" onClick={onClose}>
              <CloseOutlined sx={{ color: 'black' }} />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, maxHeight: 300, overflowY: 'auto' }}>
        {!isOnlyBars &&
          lineSeriesItems.map(item => (
            <TooltipRow key={item.seriesId} {...item} />
          ))}

        {barsWithoutVolume.length > 0 && !isOnlyBars && <Divider />}

        {barsWithoutVolume.map(item => (
          <TooltipRow key={item.seriesId} {...item} />
        ))}
      </DialogContent>

      {isOnlyBars && volumeItem && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            bgcolor: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.divider}`,
            px: 2,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{         display: 'flex',
        flexDirection: 'row'}}>                
            <Box
            sx={{
              width: 12,
              height: 12,
              marginTop: '4px',
              borderRadius: '50%',
              backgroundColor: theme.palette.darkerGrey.light,
              flexShrink: 0,
              marginRight: 1,
            }}
          />
            <Typography sx={{  fontSize: '14px',fontWeight: 600  }}>
              {volumeItem.formattedLabel} {volumeItem.formattedValue}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row'}} >
          <Typography sx={{  fontSize: '14px',fontWeight: 600, marginRight: '8px' }}>
            {t("screens.dashboarding.charts.total")} 
            </Typography>
            <Typography>
            {formatValue(totalValue, true, currency, locale) || NO_VALUE_MARK}
          </Typography>
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

export default NavigationTooltip;
